import { createContext, useContext, useState, useEffect } from "react";
import { getAccessToken, clearTokens, storeTokens, getUserInfo } from "../auth.js";

const AuthContext = createContext();

export function AuthProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    // Profile of the logged-in user (id, email, is_teacher, ...); null while
    // logged out or until the fetch resolves.
    const [user, setUser] = useState(null);

    useEffect(() => {
        // Check if the user has a valid access token
        const token = getAccessToken();
        setIsAuthenticated(!!token);
        setLoading(false);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) {
            setUser(null);
            return;
        }
        let cancelled = false;
        getUserInfo()
            .then((info) => {
                if (!cancelled) setUser(info);
            })
            .catch(() => {
                // Token may be stale; keep user null, authenticatedFetch will
                // handle the refresh/login redirect on the next real request.
                if (!cancelled) setUser(null);
            });
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated]);

    const login = (accessToken, refreshToken) => {
        storeTokens(accessToken, refreshToken);
        setIsAuthenticated(true);
    };

    const logout = () => {
        clearTokens();
        setIsAuthenticated(false);
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                loading,
                user,
                isTeacher: !!user?.is_teacher,
                login,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
